/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo, useRef, useState} from 'react';
import {makeStyles} from '@material-ui/core';
import {CellEditingStartedEvent, CellEditingStoppedEvent, ColDef, GridApi, GridReadyEvent} from 'ag-grid-community';
import {AgGridReact} from 'ag-grid-react';
import classNames from 'classnames';
import {HEADER_HEIGHT, ROW_HEIGHT} from '@ion/dls/src/grid/IonGridUtils';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {getEditableRowsGridOverrides} from './EditableGridStyles';
import EditableRowsGridActionsCell from './EditableRowsGridActionsCell';
import {
  getGridRowsFromApi,
  getRangeRowIndexesFromApi,
  getRowsFromSelection as getRowsFromSelectionUtil,
  getSelectedRowCountFromApi as getSelectedRowCountFromApiUtil,
  GridSelectionApi
} from './EditableRowsGridSelectionUtils';
import EditableRowsGridToolbar from './EditableRowsGridToolbar';

type RowWithId = {
  rowId: string;
  errors?: Record<string, boolean>;
};

export interface EditableRowsGridLabels {
  autosize?: string;
  undo?: string;
  redo?: string;
  clone?: string;
  addRow?: string;
}

export interface EditableRowsGridProps<T extends RowWithId> {
  rows: T[];
  columnDefs: ColDef[];
  createRow: () => T;
  cloneRow: (row: T) => T;
  onRowsChange: (rows: T[]) => void;
  toolbarLeftContent?: React.ReactNode;
  className?: string;
  labels?: EditableRowsGridLabels;
  stopEditingOnBlur?: boolean;
  onCellEditingStarted?: (event: CellEditingStartedEvent<T>) => void;
}

const createRowsSnapshot = <T extends RowWithId>(sourceRows: T[]): T[] => {
  return sourceRows.map((row) => ({
    ...row,
    errors : row?.errors ? {...row.errors} : row?.errors
  }));
};

const useStyles = makeStyles(() => ({
  root : {
    width         : '100%',
    display       : 'flex',
    flexDirection : 'column',
    flex          : 1,
    minHeight     : 0
  },
  grid       : getEditableRowsGridOverrides(),
  dragHandle : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    cursor         : 'grab',
    color          : dlsColor('grey3')
  },
  deleteButton : {
    display        : 'flex',
    justifyContent : 'center',
    alignItems     : 'center',
    cursor         : 'pointer',
    color          : dlsColor('grey3')
  }
}));

/**
 * Reusable editable AG Grid wrapper with row actions and undo/redo history.
 *
 * ```typescript
 * <EditableRowsGrid
 *   rows={rows}
 *   columnDefs={columnDefs}
 *   createRow={createRow}
 *   cloneRow={cloneRow}
 *   onRowsChange={onRowsChange}
 * />
 * ```
 *
 * @param   rows                 Grid row data.
 * @param   columnDefs           AG Grid column definitions.
 * @param   createRow            Creates a new default row.
 * @param   cloneRow             Creates a cloned row from the source row.
 * @param   onRowsChange         Invoked with updated rows after grid mutations.
 * @param   toolbarLeftContent   Optional content rendered on the left side of the toolbar.
 * @param   className            Optional root class name.
 * @param   labels               Optional toolbar labels.
 * @param   stopEditingOnBlur    Controls whether grid editing stops when focus leaves a cell.
 * @param   onCellEditingStarted Optional callback fired when cell editing starts.
 * @returns The editable rows grid component.
 */
const EditableRowsGrid = <T extends RowWithId>({
  rows,
  columnDefs,
  createRow,
  cloneRow,
  onRowsChange,
  toolbarLeftContent,
  className,
  labels,
  onCellEditingStarted,
  stopEditingOnBlur = true
}: EditableRowsGridProps<T>) => {
  const classes                                 = useStyles();
  const gridRef                                 = useRef<AgGridReact<T>>(null);
  const gridApiRef                              = useRef<GridApi<T> | null>(null);
  const [past, setPast]                         = useState<T[][]>([]);
  const [future, setFuture]                     = useState<T[][]>([]);
  const [selectedRowCount, setSelectedRowCount] = useState(0);
  const undoDisabled                            = !past.length;
  const redoDisabled                            = !future.length;
  const cloneDisabled                           = !rows.length;

  const commit = useCallback((nextRows: T[], trackHistory = true) => {
    if (trackHistory) {
      setPast((value) => [...value, createRowsSnapshot(rows)]);
      setFuture([]);
    }
    onRowsChange(nextRows);
  }, [onRowsChange, rows]);

  const readGridRows = useCallback((): T[] => {
    return getGridRowsFromApi<T>(gridRef.current?.api);
  }, []);

  const getRowsFromSelection = useCallback((): T[] => {
    const api = gridRef.current?.api as GridSelectionApi;
    if (!api) {
      return [];
    }
    return getRowsFromSelectionUtil(rows, api);
  }, [rows]);

  const getSelectedRowCountFromApi = useCallback((api: GridSelectionApi): number => {
    return getSelectedRowCountFromApiUtil(api);
  }, []);

  const onUndo = useCallback(() => {
    if (!past.length) {
      return;
    }
    const previous = past[past.length - 1];
    setPast((value) => value.slice(0, -1));
    setFuture((value) => [createRowsSnapshot(rows), ...value]);
    onRowsChange(createRowsSnapshot(previous));
  }, [onRowsChange, past, rows]);

  const onRedo = useCallback(() => {
    if (!future.length) {
      return;
    }
    const [next, ...remaining] = future;
    setPast((value) => [...value, createRowsSnapshot(rows)]);
    setFuture(remaining);
    onRowsChange(createRowsSnapshot(next));
  }, [future, onRowsChange, rows]);

  const onClone = useCallback(() => {
    const selectedSources = getRowsFromSelection();
    const fallbackSource = rows[rows.length - 1];
    const sources = selectedSources.length ? selectedSources : (fallbackSource ? [fallbackSource] : []);
    if (!sources.length) {
      return;
    }
    const clones = sources.map((source) => cloneRow(source));
    const nextRows = [...rows, ...clones];
    commit(nextRows);
  }, [cloneRow, commit, getRowsFromSelection, rows]);

  const onAddRow = useCallback(() => {
    const nextRows = [...rows, createRow()];
    commit(nextRows);
  }, [commit, createRow, rows]);

  const onDeleteRow = useCallback((rowId: string) => {
    const nextRows = rows.filter((item) => item.rowId !== rowId);
    commit(nextRows);
  }, [commit, rows]);

  const onCellValueChange = useCallback(() => {
    const nextRows = readGridRows();
    commit(nextRows, false);
    gridRef.current?.api?.refreshCells({force: true});
  }, [commit, readGridRows]);

  const onGridCellEditingStopped = useCallback((event: CellEditingStoppedEvent<T>) => {
    const nextRows = getGridRowsFromApi<T>(event?.api);

    const currentSerialized = JSON.stringify(rows);
    const nextSerialized = JSON.stringify(nextRows);
    if (nextSerialized !== currentSerialized) {
      commit(nextRows);
    }
    gridRef.current?.api?.refreshCells({force: true});
  }, [commit, rows]);

  const onGridRowDragEnd = useCallback(() => {
    const nextRows = readGridRows();
    commit(nextRows);
  }, [commit, readGridRows]);

  const onRangeSelectionChanged = useCallback((event: any) => {
    const api = event?.api as GridSelectionApi;
    if (api) {
      const rowIndexes = getRangeRowIndexesFromApi(api);

      api.deselectAll?.();
      rowIndexes.forEach((rowIndex) => {
        api.getDisplayedRowAtIndex?.(rowIndex)?.setSelected?.(true);
      });
      setSelectedRowCount(getSelectedRowCountFromApi(api));
    }
  }, [getSelectedRowCountFromApi]);

  const onSelectionChanged = useCallback((event: any) => {
    const api = event?.api as GridSelectionApi;
    setSelectedRowCount(getSelectedRowCountFromApi(api));
  }, []);

  const onGridCellEditingStarted = useCallback((event: CellEditingStartedEvent<T>) => {
    onCellEditingStarted?.(event);
    window.setTimeout(() => {
      const input = document.querySelector('.ag-cell-inline-editing input') as HTMLInputElement | null;
      if (input) {
        const length = input.value?.length || 0;
        input.setSelectionRange(length, length);
      }
    }, 0);
  }, [onCellEditingStarted]);

  const onGridReady = useCallback((event: GridReadyEvent<T>) => {
    gridApiRef.current = event.api;
  }, []);

  const focusFirstEditableCell = useCallback(() => {
    const firstEditable = columnDefs?.find((column) => column?.editable !== false);
    const firstColumnId = `${firstEditable?.colId || firstEditable?.field || ''}`.trim();
    if (!firstColumnId) {
      return;
    }
    gridRef.current?.api?.setFocusedCell(0, firstColumnId);
    gridRef.current?.api?.startEditingCell({
      rowIndex : 0,
      colKey   : firstColumnId
    });
  }, [columnDefs]);

  const onFirstDataRendered = useCallback(() => {
    gridRef.current?.api?.sizeColumnsToFit();
    focusFirstEditableCell();
    setSelectedRowCount(getRowsFromSelection().length);
  }, [focusFirstEditableCell, getRowsFromSelection]);

  const getRowId = useCallback((params: {data: RowWithId}) => {
    return params.data?.rowId;
  }, []);

  const rowClassRules = useMemo(() => ({
    'row-has-error' : function(params) {
      return Object.values(params?.data?.errors || {}).some(Boolean);
    }
  }), []);

  const allColumns = useMemo<ColDef[]>(() => ([
    ...columnDefs,
    {
      colId                    : '__actions',
      headerName               : '',
      sortable                 : false,
      filter                   : false,
      resizable                : false,
      suppressMenu             : true,
      suppressHeaderMenuButton : true,
      editable                 : false,
      suppressNavigable        : true,
      suppressKeyboardEvent    : () => true,
      pinned                   : 'right',
      width                    : 84,
      maxWidth                 : 84,
      minWidth                 : 84,
      cellRenderer             : ({data, registerRowDragger}) => (
        <EditableRowsGridActionsCell data={data}
          onDeleteRow={onDeleteRow}
          className={classes.deleteButton}
          dragHandleClass={classes.dragHandle}
          registerRowDragger={registerRowDragger} />
      )
    }
  ]), [classes.deleteButton, classes.dragHandle, columnDefs, onDeleteRow]);

  const cloneLabel = selectedRowCount > 1
    ? `Clone ${selectedRowCount} Rows`
    : (labels?.clone || 'Clone Row');

  return (
    <div className={classNames(classes.root, className)}>
      <EditableRowsGridToolbar toolbarLeftContent={toolbarLeftContent}
        undoLabel={labels?.undo || 'Undo'}
        redoLabel={labels?.redo || 'Redo'}
        cloneLabel={cloneLabel}
        addRowLabel={labels?.addRow || 'Add Row'}
        undoDisabled={undoDisabled}
        redoDisabled={redoDisabled}
        cloneDisabled={cloneDisabled}
        onUndo={onUndo}
        onRedo={onRedo}
        onClone={onClone}
        onAddRow={onAddRow} />
      <div className={classNames('ag-theme-quartz', classes.grid)}>
        <AgGridReact<T> ref={gridRef}
          rowData={rows}
          columnDefs={allColumns}
          enableRangeSelection
          rowDragManaged
          suppressMoveWhenRowDragging
          animateRows
          suppressMenuHide
          rowClassRules={rowClassRules}
          getRowId={getRowId}
          rowHeight={ROW_HEIGHT}
          headerHeight={HEADER_HEIGHT}
          rowSelection='multiple'
          rowMultiSelectWithClick
          singleClickEdit
          stopEditingWhenCellsLoseFocus={stopEditingOnBlur}
          defaultColDef={{
            editable  : true,
            resizable : true,
            sortable  : false,
            filter    : false
          }}
          onCellEditingStarted={onGridCellEditingStarted}
          onCellValueChanged={onCellValueChange}
          onCellEditingStopped={onGridCellEditingStopped}
          onRowDragEnd={onGridRowDragEnd}
          onRangeSelectionChanged={onRangeSelectionChanged}
          onSelectionChanged={onSelectionChanged}
          onGridReady={onGridReady}
          onFirstDataRendered={onFirstDataRendered} />
      </div>
    </div>
  );
};

export default EditableRowsGrid;
